import { useEffect, useState, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useAdmin, type UserRow } from '@/hooks/useAdmin'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { PaginationControls } from '@/components/ui/pagination-controls'
import { Users, Search, ChevronRight, Shield, CheckCircle2, ShieldAlert } from 'lucide-react'

const PAGE_SIZE = 25

interface Filters {
  platformAdmin: boolean | undefined
  verified: boolean | undefined
  mfa: boolean | undefined
}

function ToggleChip({
  label,
  active,
  icon: Icon,
  onClick,
}: {
  label: string
  active: boolean
  icon: React.ComponentType<{ className?: string }>
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium transition-colors ${
        active
          ? 'bg-primary/10 border-primary/40 text-primary'
          : 'border-border text-muted-foreground hover:text-foreground hover:border-border/80 hover:bg-accent/40'
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  )
}

export function UsersPage() {
  const { listUsers } = useAdmin()

  const [users, setUsers] = useState<UserRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<Filters>({
    platformAdmin: undefined,
    verified: undefined,
    mfa: undefined,
  })
  const [page, setPage] = useState(1)

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetch = useCallback(
    (s: string, f: Filters, p: number) => {
      setLoading(true)
      setError(null)
      listUsers({
        search: s || undefined,
        platformAdmin: f.platformAdmin,
        verified: f.verified,
        mfa: f.mfa,
        limit: PAGE_SIZE,
        offset: (p - 1) * PAGE_SIZE,
      })
        .then(({ users: u, total: t }) => {
          setUsers(u)
          setTotal(t)
        })
        .catch(() => setError('Failed to load users'))
        .finally(() => setLoading(false))
    },
    [listUsers],
  )

  useEffect(() => {
    fetch(search, filters, page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page])

  const handleSearchChange = (value: string) => {
    setSearch(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setPage(1)
      fetch(value, filters, 1)
    }, 300)
  }

  const toggleFilter = (key: keyof Filters) => {
    const next = { ...filters, [key]: filters[key] === true ? undefined : true }
    setFilters(next)
    setPage(1)
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="space-y-6">
      <div>
        <h1 className="brand-wordmark text-2xl">Users</h1>
        <p className="kicker text-muted-foreground mt-1">
          {total.toLocaleString()} user{total !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search by email or name…"
            className="pl-8 h-9 text-sm"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ToggleChip
            label="Platform admin"
            active={filters.platformAdmin === true}
            icon={Shield}
            onClick={() => toggleFilter('platformAdmin')}
          />
          <ToggleChip
            label="Verified"
            active={filters.verified === true}
            icon={CheckCircle2}
            onClick={() => toggleFilter('verified')}
          />
          <ToggleChip
            label="MFA on"
            active={filters.mfa === true}
            icon={ShieldAlert}
            onClick={() => toggleFilter('mfa')}
          />
        </div>
        {(filters.platformAdmin || filters.verified || filters.mfa) && (
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-8 px-2 text-muted-foreground"
            onClick={() => {
              const reset: Filters = { platformAdmin: undefined, verified: undefined, mfa: undefined }
              setFilters(reset)
              setPage(1)
            }}
          >
            Clear filters
          </Button>
        )}
      </div>

      {/* Table */}
      <Card>
        {error ? (
          <CardContent className="p-6 text-sm text-destructive">{error}</CardContent>
        ) : loading ? (
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-6 py-3.5">
                  <div className="h-4 w-48 rounded bg-muted/40 animate-pulse" />
                  <div className="h-4 w-32 rounded bg-muted/30 animate-pulse" />
                  <div className="ml-auto h-4 w-20 rounded bg-muted/30 animate-pulse" />
                </div>
              ))}
            </div>
          </CardContent>
        ) : users.length === 0 ? (
          <CardContent className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
            <Users className="h-8 w-8 opacity-40" />
            <p className="text-sm">No users found</p>
          </CardContent>
        ) : (
          <CardContent className="p-0">
            {/* Header */}
            <div className="grid grid-cols-[1fr_1fr_80px_68px_60px_110px_28px] gap-4 px-6 py-2.5 border-b text-xs font-medium text-muted-foreground uppercase tracking-wide">
              <span>Email</span>
              <span>Name / Org</span>
              <span className="text-center">Admin</span>
              <span className="text-center">Verified</span>
              <span className="text-center">MFA</span>
              <span>Created</span>
              <span />
            </div>
            <div className="divide-y divide-border">
              {users.map((u) => (
                <Link
                  key={u.id}
                  to={`/users/${u.id}`}
                  className="grid grid-cols-[1fr_1fr_80px_68px_60px_110px_28px] gap-4 px-6 py-3.5 items-center hover:bg-accent/40 transition-colors group"
                >
                  <p className="text-sm font-medium truncate">{u.email}</p>
                  <div className="min-w-0">
                    <p className="text-sm truncate">{u.name || '—'}</p>
                    {u.organization_id && (
                      <p className="mono-accent text-muted-foreground truncate">{u.organization_id}</p>
                    )}
                  </div>
                  <div className="flex justify-center">
                    {u.is_platform_admin ? (
                      <Shield className="h-3.5 w-3.5 text-destructive" aria-label="Platform admin" />
                    ) : (
                      <span className="text-muted-foreground/30">—</span>
                    )}
                  </div>
                  <div className="flex justify-center">
                    {u.email_verified ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-success" aria-label="Verified" />
                    ) : (
                      <span className="text-muted-foreground/30">—</span>
                    )}
                  </div>
                  <div className="flex justify-center">
                    {u.mfa_enabled ? (
                      <ShieldAlert className="h-3.5 w-3.5 text-primary" aria-label="MFA enabled" />
                    ) : (
                      <span className="text-muted-foreground/30">—</span>
                    )}
                  </div>
                  <p className="mono-accent text-muted-foreground">
                    {new Date(u.created_at).toLocaleDateString()}
                  </p>
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50 group-hover:text-muted-foreground transition-colors" />
                </Link>
              ))}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Pagination */}
      {!loading && !error && total > 0 && (
        <PaginationControls
          page={page}
          totalPages={totalPages}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
        />
      )}
    </div>
  )
}
